import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import styles from './tab-wrapper.module.css'

const propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    additionalTabProperties: PropTypes.object,
    children: PropTypes.element.isRequired,
    onClick: PropTypes.func.isRequired
}

const defaultProps = {
    additionalTabProperties: {}
}

/**
 * Because the only task of the <i>Tabs</i> is rendering. We need to wrap them and add other functionality,
 * like click handling and styling.
 *
 * For the sake of the selection functionality, we need to clone the <i>Tabs</i>,
 * because we must render at least one tab as selected.
 *
 * The <code>isGreedy</code> and <code>widthInPx</code> properties also need to be set on the <code>TabWrapper</code>,
 * not only at the <i>Tab</i> where they are defined.
 *
 * @param {ReactElement} children               - the single <i>Tab</i> to wrap (only one child is allowed).
 * @param {Function} onClick                    - a click handler for handling <i>Tab</i> selection.
 * @param {Object} additionalTabProperties      - additional properties which are set to the Tab clone.
 * @return {ReactElement}
 * @constructor
 */
function TabWrapper({ additionalTabProperties, children, onClick }) {
    const tab = React.Children.only(children)
    const { isGreedy, widthInPx } = tab.props
    const style = widthInPx ? { flexBasis: `${widthInPx}px` } : {}

    const combinedClassName = cx(
        styles['tab-wrapper'],
        { [styles['is-greedy']]: isGreedy },
        { [styles['highlighted-wrapper']]: additionalTabProperties.isHighlighted },
        't3sel-tab-wrapper'
    )

    return (
        <div className={combinedClassName} style={style} onClick={onClick}>
            {React.cloneElement(tab, additionalTabProperties)}
        </div>
    )
}

TabWrapper.propTypes = propTypes
TabWrapper.defaultProps = defaultProps

export default TabWrapper
